import { MapEntity } from '../../mapTypes.d';

/**
 * Geographic bounds for rendering
 */
export interface RenderBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

/**
 * Tile coordinate in the slippy map tile scheme
 */
export interface TileCoord {
    x: number;
    y: number;
    z: number;
}

/**
 * Pixel position on canvas
 */
export interface PixelPoint {
    x: number;
    y: number;
}

/**
 * Geographic coordinate
 */
export interface LatLng {
    lat: number;
    lng: number;
}

/**
 * Options for the canvas map renderer
 */
export interface RenderOptions {
    /** Geographic bounds to render */
    bounds: RenderBounds;
    /** Output canvas width in pixels */
    widthPx: number;
    /** Output canvas height in pixels */
    heightPx: number;
    /** Map entities (military symbols) to render */
    entities: MapEntity[];
    /** Whether to show MGRS grid overlay */
    showMgrsGrid: boolean;
    /** Tile server URL template (with {s}, {z}, {x}, {y} placeholders) */
    tileUrl: string;
    /** Symbol size in pixels (default 32) */
    symbolSize?: number;
    /** MGRS grid interval in meters (100000, 10000, 1000, or 100). If not specified, auto-selects based on zoom. */
    gridInterval?: number;
}

/**
 * Progress callback phases
 */
export type RenderPhase = 'tiles' | 'grid' | 'markers' | 'complete';

/**
 * Progress information during rendering
 */
export interface RenderProgress {
    phase: RenderPhase;
    tilesLoaded: number;
    tilesTotal: number;
    percentage: number;
}

/**
 * Progress callback function type
 */
export type ProgressCallback = (progress: RenderProgress) => void;

/**
 * MGRS grid styling options
 */
export interface MgrsGridOptions {
    /** GZD boundary line color */
    gzdColor: string;
    /** GZD boundary line width */
    gzdWeight: number;
    /** GZD boundary dash pattern */
    gzdDashArray: number[];
    /** GZD label font */
    gzdFont: string;
    /** GZD label text color */
    gzdFontColor: string;
    /** 100km grid line color */
    hkColor: string;
    /** 100km grid dash pattern */
    hkDashArray: number[];
    /** Regular grid line color */
    gridColor: string;
    /** Regular grid line weight */
    gridWeight: number;
    /** Grid label font */
    gridFont: string;
    /** Grid label text color */
    gridFontColor: string;
    /** Minimum zoom for 100km grid */
    hundredKMinZoom: number;
    /** Minimum zoom for 10km grid */
    tenKMinZoom: number;
    /** Minimum zoom for 1km grid */
    oneKMinZoom: number;
    /** Minimum zoom for 100m grid */
    hundredMMinZoom: number;
}

/**
 * Default MGRS grid options
 */
export const DEFAULT_MGRS_OPTIONS: MgrsGridOptions = {
    gzdColor: '#888888',
    gzdWeight: 1.5,
    gzdDashArray: [6, 6],
    gzdFont: '14px Courier New',
    gzdFontColor: '#ffffff',
    hkColor: '#990000',
    hkDashArray: [4, 4],
    gridColor: '#000000',
    gridWeight: 1,
    gridFont: '14px Courier New',
    gridFontColor: '#ffffff',
    hundredKMinZoom: 6,
    tenKMinZoom: 9,
    oneKMinZoom: 12,
    hundredMMinZoom: 15,
};

/**
 * UTM coordinate
 */
export interface UtmCoord {
    easting: number;
    northing: number;
    zoneNumber: number;
    zoneLetter: string;
}
